import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'

import { withStyles } from '@material-ui/core/styles'
import {
  Typography,
  Card,
  CardActions,
  CardContent,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Input,
} from '@material-ui/core'

import {
  mdiContentSave,
  mdiDeleteForever,
  mdiPencil,
} from '@mdi/js'

import TimeseriesSelectContainer from '../controls/TimeseriesSelectContainer'
import TimeseriesTypesSelectContainer from '../controls/TimeseriesTypesSelectContainer'

import deviceConfigViews from './config'
import Link from '../controls/Link'

class DeviceDetailsView extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    device: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onAddUnitTimeseries: PropTypes.func.isRequired,
    onSaveUnitTimeseries: PropTypes.func.isRequired,
    timeseries: PropTypes.object.isRequired,
  }

  static styles = (theme) => ({
    root: {
      marginTop: theme.spacing(3),
    },
    sensorsTable: {
      width: 'auto',
      marginBottom: theme.spacing(3),
    },
  })

  state = {
    isEditing: false,
    device: this.props.device,
    tsAdds: {},
    tsUpdates: {},
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ device: nextProps.device })
  }

  onEditClick = () => {
    this.setState({ isEditing: true })
  }

  onSaveClick = () => {
    const { device, tsAdds, tsUpdates } = this.state
    // TODO: it'd be best to do this after the save completes.
    // Maybe isEditing should be a prop of the device/store?
    this.setState({ isEditing: false })

    this.props.onUpdate(device.id, {
      unit_id: device.unit_id,
      target_config: device.target_config,
    })

    Object.entries(tsUpdates).forEach(([sid, timeseriesId]) => {
      this.props.onSaveUnitTimeseries(timeseriesId, device.id, sid)
    })

    Object.entries(tsAdds).forEach(([sid, name]) => {
      this.props.onAddUnitTimeseries(device.unit_id, device.id, sid, name)
    })
  }

  onRemoveClick = () => {
    const { device } = this.state
    this.props.onRemove(device)
  }

  onSensorNameChange = (index, val) => {
    const { device, tsAdds } = this.state
    const sensor = device.sensors[index]
    tsAdds[sensor.id] = val
    this.setState({ tsAdds })
  }

  onReplaceChange = (index, val) => {
    const { device, tsUpdates } = this.state
    const sensor = device.sensors[index]
    tsUpdates[sensor.id] = val
    this.setState({ tsUpdates })
  }

  onUnitIdChange = (e) => {
    const val = parseInt(e.currentTarget.value, 10)
    // eslint-disable-next-line camelcase
    const unit_id = Number.isNaN(val) ? '' : val

    this.setState(
      (prevState) => ({
        device: {
          ...prevState.device,
          unit_id,
        },
      }),
    )
  }

  onChangeConfig = (config) => {
    this.setState(
      (prevState) => ({
        device: {
          ...prevState.device,
          target_config: config,
        },
      }),
    )
  }

  linkToLog = (device) => {
    if (device.type === 'hub') {
      return (
        <Link to={`https://papertrailapp.com/systems/${device.id}`}>
          View log
        </Link>
      )
    }
    return null
  }

  timeseriesFromSensor = (sensor) => {
    const { device } = this.state
    const { timeseries } = this.props

    return Object.values(timeseries).find(({ device_id: deviceId, sensor_id: sensorId }) => (
      deviceId === device.id && sensorId === sensor.id
    ))
  }

  renderSensor = (s, i) => {
    const { isEditing, tsAdds, tsUpdates } = this.state
    const timeseries = this.timeseriesFromSensor(s)
    const tsName = (timeseries || { name: tsAdds[s.id] || '' }).name

    let timeseriesHtml
    if (timeseries) {
      timeseriesHtml = tsName
    } else {
      timeseriesHtml = (
        <TimeseriesTypesSelectContainer
          autoWidth
          disabled={!isEditing}
          value={tsName}
          sensorType={s.type}
          onChange={(val) => this.onSensorNameChange(i, val)}
        />
      )
    }

    let replaceTimeseriesHtml
    if (isEditing && !timeseries) {
      replaceTimeseriesHtml = (
        <TimeseriesSelectContainer
          unitId={this.state.device.unit_id || 0}
          value={tsUpdates[s.id] || 0}
          onChange={(val) => this.onReplaceChange(i, val)}
        />
      )
    }

    return (
      <TableRow key={s.id}>
        <TableCell>{s.id}</TableCell>
        <TableCell>{s.type}</TableCell>
        <TableCell align="right">{String(s.current_value.value)}</TableCell>
        <TableCell>
          {timeseriesHtml}
        </TableCell>
        <TableCell>
          {replaceTimeseriesHtml}
        </TableCell>
      </TableRow>
    )
  }

  renderConfig = (device, isEditing) => {
    const View = deviceConfigViews[device.type]
    if (View) {
      return (
        <>
          <Typography variant="subtitle1">Config:</Typography>
          <View
            device={device}
            isEditing={isEditing}
            onChange={this.onChangeConfig}
          />
        </>
      )
    }

    return null
  }

  render() {
    const { classes } = this.props
    const { device, isEditing } = this.state

    const sensorsHtml = device.sensors.map(this.renderSensor)

    let actionButtonHtml
    if (isEditing) {
      actionButtonHtml = (
        <IconButton aria-label="Save" onClick={this.onSaveClick}>
          <SvgIcon>
            <path d={mdiContentSave} />
          </SvgIcon>
        </IconButton>
      )
    } else {
      actionButtonHtml = (
        <IconButton aria-label="Edit" onClick={this.onEditClick}>
          <SvgIcon>
            <path d={mdiPencil} />
          </SvgIcon>
        </IconButton>
      )
    }
    const removeButtonHtml = (
      <IconButton aria-label="Remove" onClick={this.onRemoveClick}>
        <SvgIcon>
          <path d={mdiDeleteForever} />
        </SvgIcon>
      </IconButton>
    )
    return (
      <Card className={classes.root} elevation={0}>
        <CardContent>
          <Typography variant="h6">{device.id}</Typography>
          {this.linkToLog(device)}
          <Typography variant="subtitle1">Type: {device.type}</Typography>
          <Typography variant="body2">Last Updated: {moment(device.last_updated).format()}</Typography>
          <Input disabled={!isEditing} value={device.unit_id || ''} onChange={this.onUnitIdChange} />

          <Typography variant="subtitle1">Sensors:</Typography>
          <Table className={classes.sensorsTable}>
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Current Value</TableCell>
                <TableCell>Name</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {sensorsHtml}
            </TableBody>
          </Table>
          {this.renderConfig(device, isEditing)}
        </CardContent>
        <CardActions disableSpacing>
          {actionButtonHtml}
          {removeButtonHtml}
        </CardActions>
      </Card>
    )
  }
}

export default withStyles(DeviceDetailsView.styles)(DeviceDetailsView)
